import { useState, useCallback } from 'react'
import { LogViewerControls } from './LogViewerControls'
import { VirtualLogList } from './VirtualLogList'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { EmptyState } from '@/components/common/EmptyState'
import { FileText } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface LogEntry {
  /** Line number */
  lineNumber: number
  /** Timestamp */
  timestamp?: string
  /** Log level */
  level?: 'debug' | 'info' | 'warn' | 'error'
  /** Log content */
  content: string
}

export interface LogViewerProps {
  /** Log entries */
  logs: LogEntry[]
  /** Whether logs are loading */
  isLoading?: boolean
  /** Whether more logs are available */
  hasMore?: boolean
  /** Load more handler */
  onLoadMore?: () => void
  /** Download handler */
  onDownload?: () => void
  /** Whether auto-scroll is enabled */
  autoScroll?: boolean
  /** Additional CSS classes */
  className?: string
}

/**
 * Full log viewer with controls and virtualized scrolling.
 * 
 * @example
 * ```tsx
 * <LogViewer
 *   logs={logs}
 *   isLoading={isLoading}
 *   onDownload={() => downloadLogs()}
 *   autoScroll
 * />
 * ```
 */
export function LogViewer({
  logs,
  isLoading = false,
  hasMore = false,
  onLoadMore,
  onDownload,
  autoScroll: initialAutoScroll = true,
  className,
}: LogViewerProps) {
  const [filter, setFilter] = useState('')
  const [showTimestamps, setShowTimestamps] = useState(true)
  const [autoScroll, setAutoScroll] = useState(initialAutoScroll)
  const [wrapLines, setWrapLines] = useState(false)

  // Filter logs based on search
  const filteredLogs = filter
    ? logs.filter((log) =>
        log.content.toLowerCase().includes(filter.toLowerCase())
      )
    : logs

  const handleCopy = useCallback(() => {
    const text = filteredLogs.map((log) => log.content).join('\n')
    navigator.clipboard.writeText(text)
  }, [filteredLogs])

  if (isLoading && logs.length === 0) {
    return (
      <div className={cn('flex items-center justify-center h-96', className)}>
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!isLoading && logs.length === 0) {
    return (
      <EmptyState
        icon={FileText}
        title="No Logs Available"
        description="Logs will appear here once the run starts executing."
        className={className}
      />
    )
  }

  return (
    <div className={cn('flex flex-col h-full', className)}>
      <LogViewerControls
        filter={filter}
        onFilterChange={setFilter}
        showTimestamps={showTimestamps}
        onShowTimestampsChange={setShowTimestamps}
        autoScroll={autoScroll}
        onAutoScrollChange={setAutoScroll}
        wrapLines={wrapLines}
        onWrapLinesChange={setWrapLines}
        onCopy={handleCopy}
        onDownload={onDownload}
        totalLines={logs.length}
        filteredLines={filteredLogs.length}
      />
      <VirtualLogList
        logs={filteredLogs}
        showTimestamps={showTimestamps}
        autoScroll={autoScroll}
        wrapLines={wrapLines}
        isLoading={isLoading}
        hasMore={hasMore}
        onLoadMore={onLoadMore}
      />
    </div>
  )
}
